
































































































import {
  defineComponent,
  ref,
  computed,
  PropType,
  nextTick,
} from "@vue/composition-api";
import Popover from "@/components/Popover.vue";
import Table from "@/components/Table.vue";
import {
  Role,
  Organization,
  UserRoleAssignment,
  useGetSelectableRolesQuery,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import { TableField } from "@/types/TableField";
import useUserRoleAssignmentMutations from "../Composables/useUserRoleAssignmentMutations";
import ExpirationTimeSelectorPopup from "@/components/ExpirationTimeSelectorPopup.vue";
import { DateTime } from "luxon";

export default defineComponent({
  emits: ["roleAssigned", "roleRemove"],
  components: { Popover, Table, ExpirationTimeSelectorPopup },
  props: {
    assigneeRoles: {
      type: Array as PropType<UserRoleAssignment[]>,
      default: () => [],
    },
    organization: {
      type: Object as PropType<Organization>,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
    loadingRemoveRoleAssignment: {
      type: Boolean,
      required: true,
    },
    isUserAssignable: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit, root }) {
    const show = ref(false);
    const { result, loading } = useGetSelectableRolesQuery({
      fetchPolicy: "cache-first",
    });

    const expirationTimeSelectorPopup =
      ref<InstanceType<typeof ExpirationTimeSelectorPopup>>();

    const timeLimitedPopupTarget = ref("");
    const expirationDatesByRole = ref<{ [id: string]: string | undefined }>({});

    const { add, loading: mutationLoading } = useUserRoleAssignmentMutations();

    function isAssigned(role: Role) {
      return props.assigneeRoles.some(
        (element) =>
          element.role?.id == role.id &&
          element.organization?.id == props.organization?.id
      );
    }

    // Functions
    async function addRoleAssignee(role: Role) {
      var result = await add({
        roleId: role.id,
        userId: props.userId,
        expirationTime:
          expirationDatesByRole.value[role.attribute ?? ""] ?? undefined,
      });
      if (result?.errors == null)
        emit("roleAssigned", {
          expirationDate:
            expirationDatesByRole.value[role.attribute ?? ""] ?? undefined,
          role: role,
          organization: props.organization,
        });

      expirationDatesByRole.value = {
        ...expirationDatesByRole.value,
        [role.attribute ?? ""]: undefined,
      };
    }

    function removeRoleAssignee(role: Role) {
      emit("roleRemove", {
        role: role,
        organization: props.organization,
      });
    }

    const onShowTimeLimitedPopup = async (roleAttribute: string) => {
      timeLimitedPopupTarget.value = `${roleAttribute}`;
      await nextTick();
      expirationTimeSelectorPopup.value?.onOpen();
    };

    const onUpdateExpirationDate = (expiration: any) => {
      expirationDatesByRole.value = {
        ...expirationDatesByRole.value,
        [expiration.elementId]: expiration.expirationDate,
      };
    };

    const addTimeLimitedButtonText = (roleAttribute: string) => {
      const expirationDate = expirationDatesByRole.value[roleAttribute];
      if (expirationDate) {
        const date = DateTime.fromISO(expirationDate, { zone: "utc" });
        return date.toFormat("MM/dd/yyyy");
      }
      return root.$tc("common.add_time_limited");
    };

    return {
      show,
      loading,
      mutationLoading: computed(
        () => mutationLoading.value || props.loadingRemoveRoleAssignment
      ),
      roles: useResult(result, [], (data) => data.roles.nodes),
      fields: computed<TableField[]>(() => [
        { key: "attribute", filter: true, focus: true },
      ]),
      isAssigned,
      addRoleAssignee,
      removeRoleAssignee,
      timeLimitedPopupTarget,
      onShowTimeLimitedPopup,
      onUpdateExpirationDate,
      expirationTimeSelectorPopup,
      expirationDatesByRole,
      addTimeLimitedButtonText,
    };
  },
});
